/**
 * Variant Picker Vue Component
 * 
 * A Vue 3-based variant picker that loads all product data upfront
 * and handles variant selection without additional API calls.
 * 
 * Features:
 * - All variant data loaded on page load
 * - Reactive price updates
 * - Selling plan support
 * - Availability tracking
 * - Event dispatching for cart integration
 * - Backward compatible with standard variant picker events
 */

// SKU-based flavor descriptions
const SKU_FLAVOR_DESCRIPTIONS = {
  'PR039': 'Red Bell Pepper & Onion, Green Pea & Chives, Lentil Curry, Chickpea & Leeks, Carrot Ginger',
  'PR001': 'Minestrone, Tomato, Mushroom, Vegetable, Minestrone Quinoa',
  'PR004': 'Our best soups, Fasting Bars, Fasting Shake, and Extra Virgin Olive Oil.',
  'PR005': 'The same science-backed fasting benefits, now in 100% organic, pre-cooked soups for enhanced taste, ease, and convenience. No mixing required.',
  'PR038': 'Green Pea & Chives, Chickpea & Leeks - All new flavors!',
  'PR020': 'Tomato, Butternut Squash',
  'PR055': 'Tomato, Minestrone & Quinoa',
  'PR056': 'Vegetable, Red Bell Pepper & Onion',
  'PR057': 'Carrot Ginger, Lentil Curry',
  'PR069': 'Minestrone, Artichoke & Yellow Tomato',
  'PR070': 'Minestrone, Broccoli & Quinoa',
  'FS1001': 'Vanilla - 14 Individual sachets per bag',
  'FS1002': 'Chocolate - 12 Individual sachets per bag',
  'SDBundle3': '3 Individual Jars',
  'SDBundle2': '2 Individual Jars',
  'SD001': '1 Individual Jar',
  'FB1712': '12ct. Chocolate Chip',
  'FB1112': '12ct. Nuts & Honey',
  'FB1612': '12ct. Coconut Macadamia',
  'RD1002': 'Variety Pack'
};
const SKU_FLAVOR_title = {
  'PR004': 'Expanded Variety',
  'PR001': 'Original: Classic Flavors',
  'PR039': 'Original: Bold Flavors',
  'PR005': 'Next Gen'
}; 

const title_FLAVOR_DESCRIPTIONS = {
  'Variety 1':'Green Peas & Chives, Chickpea & Leeks',
  'Variety 2':'Tomato, Minestrone & Quinoa',
  'Variety 3':'Vegetable, Red Bell Pepper & Onion',
  'Variety 4':'Carrot Ginger, Lentil Curry',
  'Original':'The original powdered soups in familiar flavors: Minestrone, Tomato, Mushroom, Vegetable, and Minestrone Quinoa.',
  'Gen3':'Same powdered format, now with bold flavors like Red Bell Pepper & Onion, Green Pea & Chives, Lentil Curry, Chickpea & Leeks, and Carrot Ginger.',
  'Expanded Variety':'A curated mix of classic and bold powdered soups, plus a Fasting Shake and Fasting Bars to add variety during your fast.',
  'Next Gen':'The same science-backed fasting benefits, now in 100% organic, pre-cooked soups for enhanced taste, ease, and convenience. No mixing required.'
};
 
// Get flavor description by SKU
function getFlavorDescriptionBySKU(sku) {
  if (!sku) return '';
  return SKU_FLAVOR_DESCRIPTIONS[sku] || '';
}

// Get flavor title by SKU
function getFlavorTitleBySKU(sku) {
  if (!sku) return '';
  return SKU_FLAVOR_title[sku] || '';
}

// Theme Events - matching the standard variant picker
class ThemeEvents {
  static variantSelected = 'variant:selected';
  static variantUpdate = 'variant:update';
}

class VariantSelectedEvent extends Event {
  constructor(resource) {
    super(ThemeEvents.variantSelected, { bubbles: true });
    this.detail = { resource };
  }
}

class VariantUpdateEvent extends Event {
  constructor(resource, sourceId, data) {
    super(ThemeEvents.variantUpdate, { bubbles: true });
    this.detail = {
      resource: resource || null,
      sourceId,
      data: {
        html: data.html,
        productId: data.productId,
        newProduct: data.newProduct,
      },
    };
  }
}

// Wait for Vue to be loaded
function initVariantPickers() {
  if (typeof Vue === 'undefined') {
    console.error('Vue is not loaded');
    return;
  }

  const { createApp, ref, computed, watch, onMounted } = Vue;

  // Find all variant picker components
  const variantPickers = document.querySelectorAll('variant-picker-vue');

  variantPickers.forEach((picker) => {
    const dataScript = picker.querySelector('script[data-product-data]');
    if (!dataScript) {
      console.error('No product data found for variant picker');
      return;
    }

    let productData;
    try {
      productData = JSON.parse(dataScript.textContent);
    } catch (e) {
      console.error('Error parsing product data:', e);
      return;
    }

    const appId = `variant-picker-app-${productData.product.id}`;
    const appContainer = picker.querySelector(`#${appId}`);

    if (!appContainer) {
      console.error('App container not found:', appId);
      return;
    }

    // Get the pre-rendered form
    const existingForm = appContainer.querySelector('.variant-picker__form');
    if (!existingForm) {
      console.error('Pre-rendered form not found');
      return;
    }

    // Create Vue app
    const app = createApp({
      setup() {
        // Reactive state
        const product = ref(productData.product);
        const variants = ref(productData.variants);
        const settings = ref(productData.settings);
        const moneyFormat = ref(productData.moneyFormat);
        
        // Create variant lookup map for fast access
        const variantMap = ref(new Map());
        variants.value.forEach(variant => {
          variantMap.value.set(variant.id, variant);
        });

        // Selected options (option values, not variant ID)
        const selectedOptions = ref({});

        // Initialize selected options from selected variant OR from DOM
        const initializeSelectedOptions = () => {
          // First, try to get from selected variant
          const selectedVariantId = productData.selectedVariantId;
          const selectedVariant = variants.value.find(v => v.id === selectedVariantId);
          
          if (selectedVariant) {
            product.value.optionsWithValues.forEach((option, index) => {
              const optionValue = selectedVariant[`option${index + 1}`];
              selectedOptions.value[option.name] = optionValue;
            });
            
            devLog('Initialized selected options from variant:', {
              variantId: selectedVariant.id,
              variantTitle: selectedVariant.title,
              selectedOptions: { ...selectedOptions.value }
            });
          } else {
            // Fallback: try to read from checked/selected inputs in DOM
            const checkedRadios = appContainer.querySelectorAll('input[type="radio"]:checked');
            checkedRadios.forEach(radio => {
              const card = radio.closest('.variant-option__radio-card');
              const label = radio.closest('label[data-option-name]');
              
              if (card && card.dataset.optionName) {
                selectedOptions.value[card.dataset.optionName] = radio.value;
              } else if (label && label.dataset.optionName) {
                selectedOptions.value[label.dataset.optionName] = radio.value;
              }
            });
            
            const selectedSelects = appContainer.querySelectorAll('select[data-option-name]');
            selectedSelects.forEach(select => {
              if (select.dataset.optionName && select.value) {
                selectedOptions.value[select.dataset.optionName] = select.value;
              }
            });
            
            devLog('Initialized selected options from DOM:', {
              selectedOptions: { ...selectedOptions.value }
            });
          }
        };

        // Current selected variant (computed)
        const currentVariant = computed(() => {
          // Check if we have selections for all options
          const allOptionsSelected = product.value.optionsWithValues.every(option => {
            return selectedOptions.value[option.name] !== undefined && 
                   selectedOptions.value[option.name] !== null &&
                   selectedOptions.value[option.name] !== '';
          });

          if (!allOptionsSelected) {
            devLog('Not all options selected yet:', {
              selectedOptions: { ...selectedOptions.value },
              requiredOptions: product.value.optionsWithValues.map(o => o.name)
            });
            return variants.value[0]; // Return first variant as fallback
          }

          // Find variant that matches all selected options
          const matchedVariant = variants.value.find(variant => {
            const matches = product.value.optionsWithValues.every((option, index) => {
              const selectedValue = selectedOptions.value[option.name];
              const variantValue = variant[`option${index + 1}`];
              const isMatch = selectedValue === variantValue;
              
              if (!isMatch) {
                devLog('Option mismatch:', {
                  optionName: option.name,
                  optionIndex: index + 1,
                  selectedValue,
                  variantValue,
                  variantId: variant.id
                });
              }
              
              return isMatch;
            });
            
            if (matches) {
              devLog('Found matching variant:', {
                id: variant.id,
                title: variant.title,
                option1: variant.option1,
                option2: variant.option2,
                option3: variant.option3
              });
            }
            
            return matches;
          });

          if (!matchedVariant) {
            console.warn('No matching variant found for:', selectedOptions.value);
            return variants.value[0];
          }

          return matchedVariant;
        });

        // The subscription percentage is optional: left at 0 it means "use the
        // one-time percentage for subscriptions too", so either setting on its
        // own is enough to turn the discounted price display on.
        const hasDiscountPercentage = () =>
          (Number(settings.value.discountPercentage) || 0) > 0 ||
          (Number(settings.value.subscriptionDiscountPercentage) || 0) > 0;

        const discountPercentageFor = (variant) => {
          const onetime = Number(settings.value.discountPercentage) || 0;
          const subscription = Number(settings.value.subscriptionDiscountPercentage) || 0;
          const isSubscription = !!(variant && variant.title && /subscri/i.test(variant.title));

          return isSubscription && subscription > 0 ? subscription : onetime;
        };

        // Calculate discounted price based on settings
        const calculateDiscountedPrice = (originalPrice, variant) => {
          if (!settings.value.enableDiscountDisplay || !hasDiscountPercentage()) {
            return originalPrice;
          }
          // For 20-off-sitewide: subscription gets 20% off compare-at price, one time gets 20% off price
          if (settings.value.discountOnlyOneTime) {
            if (variant?.title && variant.title.toLowerCase().includes('subscri') && settings.value.discountSubscriptionUseCompare) {
              const basePrice = variant.compareAtPrice > 0 ? variant.compareAtPrice : originalPrice;
              const discount = settings.value.discountSubscriptionPercentage / 100;
              return Math.round(basePrice * (1 - discount));
            }
            if (variant?.title && !variant.title.toLowerCase().includes('one time')) {
              return originalPrice;
            }
          }
          const discount = discountPercentageFor(variant) / 100;
          return Math.round(originalPrice * (1 - discount));
        };

        // Get the display price (discounted if enabled)
        const displayPrice = computed(() => {
          if (!currentVariant.value) return 0;
          if (settings.value.enableDiscountDisplay && settings.value.showCompareAtPrice) {
            return calculateDiscountedPrice(currentVariant.value.price, currentVariant.value);
          }
          return currentVariant.value.price;
        });

        // Get the compare at price for display
        const displayCompareAtPrice = computed(() => {
          if (!currentVariant.value) return null;
          // If showVariantCompareAtPrice is enabled, always show variant's actual compare-at price
          if (settings.value.showVariantCompareAtPrice) {
            return currentVariant.value.compareAtPrice;
          }
          // If discount display is enabled and showCompareAtPrice is true, use original price as compare-at
          if (settings.value.enableDiscountDisplay && settings.value.showCompareAtPrice && hasDiscountPercentage()) {
            return currentVariant.value.price;
          }
          // Otherwise use the actual compare at price
          return currentVariant.value.compareAtPrice;
        });

        // Formatted price
        const formattedPrice = computed(() => {
          if (!currentVariant.value) return '';
          return formatMoney(displayPrice.value, moneyFormat.value);
        });

        // Formatted compare at price
        const formattedCompareAtPrice = computed(() => {
          if (!displayCompareAtPrice.value) return null;
          return formatMoney(displayCompareAtPrice.value, moneyFormat.value);
        });

        // Has compare at price
        const hasCompareAtPrice = computed(() => {
          // If showVariantCompareAtPrice is enabled, show if variant has compare-at price
          if (settings.value.showVariantCompareAtPrice) {
            return currentVariant.value?.compareAtPrice && 
                   currentVariant.value.compareAtPrice > displayPrice.value;
          }
          if (settings.value.enableDiscountDisplay && settings.value.showCompareAtPrice && hasDiscountPercentage()) {
            return displayCompareAtPrice.value > displayPrice.value;
          } 
          return currentVariant.value?.compareAtPrice &&
                 currentVariant.value.compareAtPrice > displayPrice.value;
        });

        // Check if option value is available (has at least one available variant)
        const isOptionValueAvailable = (optionName, optionValue) => {
          return variants.value.some(variant => {
            // Check if this variant matches the option value
            const optionIndex = product.value.optionsWithValues.findIndex(o => o.name === optionName);
            if (variant[`option${optionIndex + 1}`] !== optionValue) {
              return false;
            }

            // Check if variant matches other selected options
            const matchesOtherOptions = product.value.optionsWithValues.every((option, index) => {
              if (option.name === optionName) return true; // Skip current option
              const selectedValue = selectedOptions.value[option.name];
              if (!selectedValue) return true; // No selection yet
              return variant[`option${index + 1}`] === selectedValue;
            });

            return matchesOtherOptions && variant.available;
          });
        };

        // Handle option change
        const handleOptionChange = (optionName, optionValue) => {
          // Update selected option
          selectedOptions.value[optionName] = optionValue;
          
          // Log for debugging
          devLog('Option changed:', {
            optionName,
            optionValue,
            allSelectedOptions: { ...selectedOptions.value }
          });

          // Update UI immediately to reflect new selection
          updateUI();

          // Dispatch variant change event
          if (currentVariant.value) {
            devLog('Current variant after option change:', {
              id: currentVariant.value.id,
              title: currentVariant.value.title,
              options: [currentVariant.value.option1, currentVariant.value.option2, currentVariant.value.option3].filter(Boolean)
            });
            dispatchVariantChangeEvent(currentVariant.value);
          } else {
            console.warn('No matching variant found for selected options:', selectedOptions.value);
          }

          // Update URL if on product page
          updateUrl();
        };

        // Dispatch custom events for variant change
        const dispatchVariantChangeEvent = (variant) => {
          // 1. Dispatch new variant:change event (for Vue components)
          // Include discounted prices if discount settings are enabled
          const eventPrice = displayPrice.value;
          const eventCompareAtPrice = displayCompareAtPrice.value;
          
          const changeEvent = new CustomEvent('variant:change', {
            detail: {
              variant: variant,
              productId: product.value.id,
              variantId: variant.id,
              price: eventPrice,
              compareAtPrice: eventCompareAtPrice,
              originalPrice: variant.price,
              originalCompareAtPrice: variant.compareAtPrice,
              discountSettings: {
                enabled: settings.value.enableDiscountDisplay,
                percentage: settings.value.discountPercentage,
                showCompareAtPrice: settings.value.showCompareAtPrice
              },
              available: variant.available,
              selectedOptions: selectedOptions.value,
              sellingPlanAllocations: variant.sellingPlanAllocations
            },
            bubbles: true,
            composed: true
          });
          picker.dispatchEvent(changeEvent);

          // 2. Dispatch VariantSelectedEvent (for backward compatibility)
          const selectedEvent = new VariantSelectedEvent({
            id: variant.id.toString(),
            productId: product.value.id.toString(),
            variantId: variant.id.toString()
          });
          picker.dispatchEvent(selectedEvent);

          // 3. Dispatch VariantUpdateEvent (for backward compatibility with subscription widget)
          // Format the variant resource to match what subscription widget expects
          // Use discounted prices if enabled
          const variantResource = {
            id: variant.id,
            title: variant.title,
            available: variant.available,
            price: eventPrice,
            compare_at_price: eventCompareAtPrice,
            original_price: variant.price,
            original_compare_at_price: variant.compareAtPrice,
            productId: product.value.id.toString(),
            sku: variant.sku,
            barcode: variant.barcode,
            options: variant.options,
            option1: variant.option1,
            option2: variant.option2,
            option3: variant.option3,
            featured_media: variant.featuredImage,
            selling_plan_allocations: variant.sellingPlanAllocations
          };

          const updateEvent = new VariantUpdateEvent(
            variantResource, // resource
            picker.dataset.blockId || '', // sourceId
            {
              html: null, // No HTML needed for Vue version
              productId: product.value.id.toString(),
              newProduct: undefined
            }
          );
          picker.dispatchEvent(updateEvent);

          // Log for debugging
          devLog('Variant events dispatched:', {
            variantId: variant.id,
            price: eventPrice,
            compareAtPrice: eventCompareAtPrice,
            discountEnabled: settings.value.enableDiscountDisplay,
            events: ['variant:change', 'variant:selected', 'variant:update']
          });
        };

        // Update URL with variant parameter
        const updateUrl = () => {
          const isOnProductPage = picker.dataset.templateProductMatch === 'true';
          
          if (!isOnProductPage || !currentVariant.value) return;

          const url = new URL(window.location.href);
          url.searchParams.set('variant', currentVariant.value.id);

          if (url.href !== window.location.href) {
            history.replaceState({}, '', url.toString());
          }
        };

        // Format money helper
        const formatMoney = (cents, format) => {
          if (typeof cents === 'string') {
            cents = cents.replace('.', '');
          }
          let value = '';
          const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
          const formatString = format || '${{amount}}';

          function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
            if (isNaN(number) || number == null) return '0';

            number = (number / 100.0).toFixed(precision);

            const parts = number.split('.');
            const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
            const cents = parts[1] ? decimal + parts[1] : '';

            return dollars + cents;
          }

          switch (formatString.match(placeholderRegex)[1]) {
            case 'amount':
              value = formatWithDelimiters(cents, 2);
              break;
            case 'amount_no_decimals':
              value = formatWithDelimiters(cents, 0);
              break;
            case 'amount_with_comma_separator':
              value = formatWithDelimiters(cents, 2, '.', ',');
              break;
            case 'amount_no_decimals_with_comma_separator':
              value = formatWithDelimiters(cents, 0, '.', ',');
              break;
          }

          return formatString.replace(placeholderRegex, value);
        };

        // Get swatch for option value
        const getSwatchStyle = (optionValue) => {
          // This would need swatch data from theme settings
          // For now, return basic style
          return {};
        };

        // Watch for variant changes
        watch(currentVariant, (newVariant, oldVariant) => {
          if (newVariant && newVariant.id !== oldVariant?.id) {
            devLog('Variant changed:', newVariant);
            updateUI();
          }
        });

        // Watch for selected options changes
        watch(selectedOptions, () => {
          updateUI();
        }, { deep: true });

        // Attach event listeners to existing inputs
        const attachEventListeners = () => {
          // Radio buttons (including radio-with-description)
          const radioInputs = appContainer.querySelectorAll('input[type="radio"]');
          radioInputs.forEach(input => {
            input.addEventListener('change', (e) => {
              // Get the parent card, label, or variant option div
              const card = e.target.closest('.variant-option__radio-card');
              const label = e.target.closest('label');
              const variantOptionDiv = e.target.closest('[data-variant-option][data-option-name]');
              
              if (card) {
                // Radio-with-description style
                const optionName = card.dataset.optionName;
                const optionValue = e.target.value;
                if (optionName && optionValue) {
                  handleOptionChange(optionName, optionValue);
                }
              } else if (label) {
                // Standard button style
                const optionName = label.dataset.optionName;
                const optionValue = e.target.value;
                if (optionName && optionValue) {
                  handleOptionChange(optionName, optionValue);
                }
              } else if (variantOptionDiv) {
                // Image grid single style
                const optionName = variantOptionDiv.dataset.optionName;
                const optionValue = e.target.value;
                if (optionName && optionValue) {
                  handleOptionChange(optionName, optionValue);
                }
              }
            });
          });

          // Select dropdowns
          const selectInputs = appContainer.querySelectorAll('select[data-option-name]');
          selectInputs.forEach(select => {
            select.addEventListener('change', (e) => {
              const optionName = e.target.dataset.optionName;
              const optionValue = e.target.value;
              handleOptionChange(optionName, optionValue);
            });
          });
        };

        // Update UI based on state
        const updateUI = () => {
          // Update selected state on all option elements (labels and cards)
          const labels = appContainer.querySelectorAll('label[data-option-name]'); 
          labels.forEach(label => {
            const optionName = label.dataset.optionName;
            const optionValue = label.dataset.optionValue;
            const isSelected = selectedOptions.value[optionName] === optionValue;
            const isAvailable = isOptionValueAvailable(optionName, optionValue);
            
            if (isSelected) {
              label.classList.add('variant-option__button-label--selected');
            } else {
              label.classList.remove('variant-option__button-label--selected');
            }  
 
            if (!isAvailable) {
              label.classList.add('variant-option__button-label--unavailable');
              const input = label.querySelector('input');
              if (input) input.disabled = true;
            } else {
              label.classList.remove('variant-option__button-label--unavailable');
              const input = label.querySelector('input');
              if (input) input.disabled = false;
            }

            // Override title with SKU title if show_sku_title is enabled
            const showSkuTitle = label.dataset.showSkuTitle === 'true';
            const titleElement = label.querySelector('.variant-option__button-label__text');
            if (showSkuTitle && titleElement) {
              const variantSku = label.dataset.variantSku;
              const skuTitle = getFlavorTitleBySKU(variantSku);
              if (skuTitle) {
                // Store original title if not already stored
                if (!label.dataset.originalTitle) {
                  label.dataset.originalTitle = titleElement.textContent;
                }
                titleElement.textContent = skuTitle;
              } else if (label.dataset.originalTitle) {
                // Restore original title if no SKU match
                titleElement.textContent = label.dataset.originalTitle;
              }
            } else if (titleElement && label.dataset.originalTitle) {
              // Restore original title if setting is disabled
              titleElement.textContent = label.dataset.originalTitle;
            }

            // Update shared description container when this is the selected label
            if (isSelected) {
              const fieldset = label.closest('.variant-option--buttons');
              const descriptionContainer = fieldset ? fieldset.querySelector('[data-description-container]') : null;
              
              if (descriptionContainer) {
                // SKU-based description
                const showDescription = label.dataset.showDescription === 'true';
                if (showDescription) {
                  const variantSku = label.dataset.variantSku;
                  const description = getFlavorDescriptionBySKU(variantSku);
                  if (description) {
                    descriptionContainer.textContent = description;
                    descriptionContainer.style.display = '';
                  } else {
                    descriptionContainer.textContent = '';
                    descriptionContainer.style.display = 'none';
                  }
                }

                // Title-based description (overrides SKU if both enabled)
                const showTitleDescription = label.dataset.showTitleDescription === 'true';
                if (showTitleDescription) {
                  const titleDescription = title_FLAVOR_DESCRIPTIONS[optionValue] || '';
                  if (titleDescription) {
                    descriptionContainer.textContent = titleDescription;
                    descriptionContainer.style.display = '';
                  } else {
                    descriptionContainer.textContent = '';
                    descriptionContainer.style.display = 'none';
                  }
                }
              }
            }
          });

          // Update radio-with-description cards
          const cards = appContainer.querySelectorAll('.variant-option__radio-card[data-option-name]');
          cards.forEach(card => {
            const optionName = card.dataset.optionName;
            const optionValue = card.dataset.optionValue;
            const isSelected = selectedOptions.value[optionName] === optionValue;
            const isAvailable = isOptionValueAvailable(optionName, optionValue);
            
            const input = card.querySelector('input');
            if (input && isSelected) {
              input.checked = true;
            }

            if (!isAvailable && input) {
              input.disabled = true;
              card.classList.add('variant-option__radio-card--unavailable');
            } else if (input) {
              input.disabled = false;
              card.classList.remove('variant-option__radio-card--unavailable');
            }

            // Update price display for this option value based on matching variant
            const priceElement = card.querySelector('.variant-option__price');
            const compareAtPriceElement = card.querySelector('.variant-option__compare_at_price');
            if (priceElement) {
              // Find variant that matches this option value and other selected options
              const matchingVariant = variants.value.find(variant => {
                // Check if variant matches this option value
                const optionIndex = product.value.optionsWithValues.findIndex(o => o.name === optionName);
                if (variant[`option${optionIndex + 1}`] !== optionValue) {
                  return false;
                }
                 
                // Check if variant matches all other selected options
                return product.value.optionsWithValues.every((option, index) => {
                  if (option.name === optionName) return true; // Skip current option
                  const selectedValue = selectedOptions.value[option.name];
                  if (!selectedValue) return true; // No selection yet
                  return variant[`option${index + 1}`] === selectedValue;
                });
              });

              if (matchingVariant) {
                // Check if this is a subscription option
                const isSubscription = card.dataset.isSubscription === 'true';
                
                // Calculate discounted price for this variant if discount is enabled
                const discountedVariantPrice = calculateDiscountedPrice(matchingVariant.price, matchingVariant);
                const useDiscountedPrice = settings.value.enableDiscountDisplay && settings.value.showCompareAtPrice && hasDiscountPercentage();
                
                if (isSubscription && product.value.sellingPlanGroups && product.value.sellingPlanGroups.length > 0) {
                  // Find Loop subscription group
                  const loopGroup = product.value.sellingPlanGroups.find(group => group.app_id === '5284869');
                  
                  if (loopGroup && matchingVariant.sellingPlanAllocations) {
                    // Find allocation for this variant
                    const allocation = matchingVariant.sellingPlanAllocations.find(
                      alloc => alloc.selling_plan_group_id === loopGroup.id
                    );
                    
                    if (allocation && allocation.price_adjustments && allocation.price_adjustments.length > 0) {
                      // Use subscription price (also apply discount if enabled)
                      let originalSubscriptionPrice = allocation.price_adjustments[0].price;
                      let subscriptionPrice = originalSubscriptionPrice;
                      if (useDiscountedPrice) {
                        subscriptionPrice = calculateDiscountedPrice(subscriptionPrice, matchingVariant);
                      }
                      priceElement.textContent = formatMoney(subscriptionPrice, moneyFormat.value);

                      // Update compare at price - show original price as strikethrough for subscription
                      if (compareAtPriceElement) {
                        if (settings.value.showVariantCompareAtPrice && matchingVariant.compareAtPrice) {
                          // showVariantCompareAtPrice overrides — always show variant's actual compare-at price
                          compareAtPriceElement.textContent = formatMoney(matchingVariant.compareAtPrice, moneyFormat.value);
                          compareAtPriceElement.style.display = '';
                        } else if (useDiscountedPrice && subscriptionPrice < originalSubscriptionPrice) {
                          // When discount is applied, show original subscription price as strikethrough
                          compareAtPriceElement.textContent = formatMoney(originalSubscriptionPrice, moneyFormat.value);
                          compareAtPriceElement.style.display = '';
                        } else if (matchingVariant.compareAtPrice || matchingVariant.price > subscriptionPrice) {
                          compareAtPriceElement.textContent = formatMoney(matchingVariant.compareAtPrice || matchingVariant.price, moneyFormat.value);
                          compareAtPriceElement.style.display = '';
                        } else {
                          compareAtPriceElement.style.display = 'none';
                        }
                      }

                      // Update discount badge if it exists
                      const discountBadge = card.querySelector('.border-lightGreen span');
                      if (discountBadge && matchingVariant.price > subscriptionPrice) {
                        const discount = Math.round(((matchingVariant.price - subscriptionPrice) / matchingVariant.price) * 100);
                        discountBadge.textContent = `${discount}% OFF`;
                      }
                    } else {
                      // No subscription pricing, use regular/discounted price
                      priceElement.textContent = formatMoney(useDiscountedPrice ? discountedVariantPrice : matchingVariant.price, moneyFormat.value);
                      // Update compare at price
                      if (compareAtPriceElement) {
                        if (settings.value.showVariantCompareAtPrice && matchingVariant.compareAtPrice) {
                          compareAtPriceElement.textContent = formatMoney(matchingVariant.compareAtPrice, moneyFormat.value);
                          compareAtPriceElement.style.display = '';
                        } else if (useDiscountedPrice) {
                          compareAtPriceElement.textContent = formatMoney(matchingVariant.price, moneyFormat.value);
                          compareAtPriceElement.style.display = '';
                        } else if (matchingVariant.compareAtPrice) {
                          compareAtPriceElement.textContent = formatMoney(matchingVariant.compareAtPrice, moneyFormat.value);
                          compareAtPriceElement.style.display = '';
                        } else {
                          compareAtPriceElement.style.display = 'none';
                        }
                      }
                    }
                  } else {
                    // No subscription group or allocations, use regular/discounted price
                    priceElement.textContent = formatMoney(useDiscountedPrice ? discountedVariantPrice : matchingVariant.price, moneyFormat.value);
                    // Update compare at price
                    if (compareAtPriceElement) {
                      if (settings.value.showVariantCompareAtPrice && matchingVariant.compareAtPrice) {
                        compareAtPriceElement.textContent = formatMoney(matchingVariant.compareAtPrice, moneyFormat.value);
                        compareAtPriceElement.style.display = '';
                      } else if (useDiscountedPrice) {
                        compareAtPriceElement.textContent = formatMoney(matchingVariant.price, moneyFormat.value);
                        compareAtPriceElement.style.display = '';
                      } else if (matchingVariant.compareAtPrice) {
                        compareAtPriceElement.textContent = formatMoney(matchingVariant.compareAtPrice, moneyFormat.value);
                        compareAtPriceElement.style.display = '';
                      } else {
                        compareAtPriceElement.style.display = 'none';
                      }
                    }
                  }
                } else {
                  // Not a subscription option, use regular/discounted price
                  const priceToShow = useDiscountedPrice ? discountedVariantPrice : matchingVariant.price;
                  priceElement.textContent = formatMoney(priceToShow, moneyFormat.value);
                  
                  // Handle compare at price display
                  if (compareAtPriceElement) {
                    // If showVariantCompareAtPrice is enabled, always show variant's compare-at price if available
                    if (settings.value.showVariantCompareAtPrice && matchingVariant.compareAtPrice) {
                      compareAtPriceElement.textContent = formatMoney(matchingVariant.compareAtPrice, moneyFormat.value);
                      compareAtPriceElement.style.display = '';
                    } else if (useDiscountedPrice) {
                      // If discount is enabled, show original price as strikethrough
                      compareAtPriceElement.textContent = formatMoney(matchingVariant.price, moneyFormat.value);
                      compareAtPriceElement.style.display = '';
                    } else if (matchingVariant.compareAtPrice) {
                      // Show variant's compare-at price if it exists
                      compareAtPriceElement.textContent = formatMoney(matchingVariant.compareAtPrice, moneyFormat.value);
                      compareAtPriceElement.style.display = '';
                    } else {
                      // Hide compare at price if no discount and no compare-at price
                      compareAtPriceElement.style.display = 'none';
                    }
                  }
                }
              }
            }

            // Override title with SKU title if show_sku_title is enabled
            const showSkuTitle = card.dataset.showSkuTitle === 'true';
            const titleElement = card.querySelector('.text-body-bold.m\\:text-small-body-bold.text-darkGreen');
            if (showSkuTitle && titleElement) {
              const variantSku = card.dataset.variantSku;
              const skuTitle = getFlavorTitleBySKU(variantSku);
              if (skuTitle) {
                // Store original title if not already stored
                if (!card.dataset.originalTitle) {
                  card.dataset.originalTitle = titleElement.textContent.trim();
                }
                titleElement.textContent = skuTitle;
              } else if (card.dataset.originalTitle) {
                // Restore original title if no SKU match
                titleElement.textContent = card.dataset.originalTitle;
              }
            } else if (titleElement && card.dataset.originalTitle) {
              // Restore original title if setting is disabled
              titleElement.textContent = card.dataset.originalTitle;
            }

            // Update description for radio cards (including V26 style)
            const descriptionContent = card.querySelector('[data-description-content]');
            const descriptionSection = card.querySelector('[data-description-section]');
            if (descriptionContent && descriptionSection) {
              const showDescription = card.dataset.showDescription === 'true';
              const showTitleDescription = card.dataset.showTitleDescription === 'true';

              let descriptionText = '';

              // Title-based description takes priority when enabled
              if (showTitleDescription) {
                descriptionText = title_FLAVOR_DESCRIPTIONS[optionValue] || '';
              }

              // Fallback to SKU description when enabled
              if (!descriptionText && showDescription) {
                const variantSku = card.dataset.variantSku;
                descriptionText = getFlavorDescriptionBySKU(variantSku);
              }

              descriptionContent.textContent = descriptionText || '';
              if (descriptionText) {
                descriptionSection.style.display = '';
                card.dataset.hasDescription = 'true';
              } else {
                descriptionSection.style.display = 'none';
                card.dataset.hasDescription = 'false';
              }
            }
          });

          // Update image grid single-select variant option divs
          const variantOptionDivs = appContainer.querySelectorAll('[data-variant-option][data-option-name]');
          variantOptionDivs.forEach(variantDiv => {
            const optionName = variantDiv.dataset.optionName;
            const optionValue = variantDiv.dataset.optionValue;
            const isSelected = selectedOptions.value[optionName] === optionValue;
            const isAvailable = isOptionValueAvailable(optionName, optionValue);
            
            const input = variantDiv.querySelector('input[type="radio"]');
            if (input && isSelected) {
              input.checked = true;
            }

            // Update visual selected state
            if (isSelected) {
              variantDiv.classList.add('border-2', 'border-[#1E4036]', 'selected', 'bg-white');
            } else {
              variantDiv.classList.remove('border-2', 'border-[#1E4036]', 'selected', 'bg-white');
            }

            // Update availability
            if (!isAvailable && input) {
              input.disabled = true;
              variantDiv.classList.add('opacity-60');
            } else if (input) {
              input.disabled = false;
              variantDiv.classList.remove('opacity-60');
            }

            // Update price display for this option value
            const priceDisplay = variantDiv.querySelector('[data-price-display]');
            if (priceDisplay) {
              // Find variant that matches this option value and other selected options
              const matchingVariant = variants.value.find(variant => {
                // Check if variant matches this option value
                const optionIndex = product.value.optionsWithValues.findIndex(o => o.name === optionName);
                if (variant[`option${optionIndex + 1}`] !== optionValue) {
                  return false;
                }
                
                // Check if variant matches all other selected options
                return product.value.optionsWithValues.every((option, index) => {
                  if (option.name === optionName) return true; // Skip current option
                  const selectedValue = selectedOptions.value[option.name];
                  if (!selectedValue) return true; // No selection yet
                  return variant[`option${index + 1}`] === selectedValue;
                });
              });

              if (matchingVariant) {
                // Update price
                const discountedPrice = calculateDiscountedPrice(matchingVariant.price, matchingVariant);
                const useDiscountedPrice = settings.value.enableDiscountDisplay && settings.value.showCompareAtPrice && hasDiscountPercentage();
                const priceToShow = useDiscountedPrice ? discountedPrice : matchingVariant.price;
                priceDisplay.textContent = formatMoney(priceToShow, moneyFormat.value);

                // Update savings badge
                const savingsBadge = variantDiv.querySelector('[data-savings-badge]');
                if (matchingVariant.compareAtPrice && matchingVariant.compareAtPrice > matchingVariant.price) {
                  const savingsAmount = matchingVariant.compareAtPrice - matchingVariant.price;
                  if (savingsBadge) {
                    savingsBadge.textContent = `SAVE ${formatMoney(savingsAmount, moneyFormat.value)}`;
                    savingsBadge.style.display = '';
                  }
                } else if (savingsBadge) {
                  savingsBadge.style.display = 'none';
                }
              }
            }

            // Override label with SKU title if show_sku_title is enabled
            const showSkuTitle = variantDiv.dataset.showSkuTitle === 'true';
            const labelElement = variantDiv.querySelector('.text-small-body-bold.text-darkGreen');
            if (showSkuTitle && labelElement) {
              const variantSku = variantDiv.dataset.variantSku;
              const skuTitle = getFlavorTitleBySKU(variantSku);
              if (skuTitle) {
                // Store original title if not already stored
                if (!variantDiv.dataset.originalTitle) {
                  variantDiv.dataset.originalTitle = labelElement.textContent.trim();
                }
                labelElement.textContent = skuTitle;
              } else if (variantDiv.dataset.originalTitle) {
                // Restore original title if no SKU match
                labelElement.textContent = variantDiv.dataset.originalTitle;
              }
            } else if (labelElement && variantDiv.dataset.originalTitle) {
              // Restore original title if setting is disabled
              labelElement.textContent = variantDiv.dataset.originalTitle;
            }
          });

          // Update swatch value displays
          product.value.optionsWithValues.forEach((option) => {
            const selectedValue = selectedOptions.value[option.name];
            if (selectedValue) {
              const fieldset = Array.from(appContainer.querySelectorAll('fieldset')).find(fs => {
                const legend = fs.querySelector('legend');
                return legend && legend.textContent.trim().startsWith(option.name);
              });
              
              if (fieldset) {
                const swatchValueSpan = fieldset.querySelector('.variant-option__swatch-value');
                if (swatchValueSpan) {
                  swatchValueSpan.textContent = selectedValue;
                }
              }
            }
          });

          // Update hidden variant ID input (works for all styles)
          const hiddenInput = appContainer.closest('.custom-section-content')?.querySelector('.shopify-product-form input[name="id"]');
          if (hiddenInput && currentVariant.value) { 
             hiddenInput.value = currentVariant.value.id;
          }
        };

        // Initialize on mount
        onMounted(() => {
          initializeSelectedOptions();
          attachEventListeners();
          updateUI();
          
          // Dispatch initial variant
          if (currentVariant.value) {
            dispatchVariantChangeEvent(currentVariant.value);
          }
        });

        return {
          product,
          variants,
          settings,
          selectedOptions,
          currentVariant,
          displayPrice,
          displayCompareAtPrice,
          formattedPrice,
          formattedCompareAtPrice,
          hasCompareAtPrice,
          calculateDiscountedPrice,
          handleOptionChange,
          isOptionValueAvailable,
          getSwatchStyle,
          updateUI
        };
      }
    });

    // Mount the app - Vue will enhance the existing DOM without replacing it
    app.mount(appContainer);

    // Store app instance on element for potential cleanup
    picker.__vueApp = app;
  });
}

// Resolve the hidden selling_plan input that belongs to the same product as the
// element that changed. A landing page renders one product block per card (each
// popup has its own variant picker and its own buy-buttons form), so a
// document-wide querySelector would always return the FIRST card's input and the
// plan would be written into the wrong form — the cart then gets a one-time
// purchase. Walk up from the changed element instead and take the input inside
// the nearest block that contains one, preferring the id that matches this
// picker's product.
function findSellingPlanInput(el) {
  const picker = el.closest ? el.closest('variant-picker-vue') : null;
  const productId = picker && picker.dataset.productId;

  let node = el;
  while (node) {
    if (node.querySelector) {
      if (productId) {
        const scoped = node.querySelector(
          '#selling_plan_' + productId + '[data-selling-plan-input]'
        );
        if (scoped) return scoped;
      }

      const nearest = node.querySelector('[data-selling-plan-input]');
      if (nearest) return nearest;
    }
    node = node.parentElement;
  }

  return null;
}

// Handle subscription/selling plan selection
function initSellingPlanHandlers() {
  // Handle subscription option selection (radio buttons)
  document.addEventListener('change', (e) => {
    const radioInput = e.target;
    if (!radioInput.classList.contains('variant-option__radio-input')) return;

    const card = radioInput.closest('.variant-option__radio-card');
    if (!card) return;

    const isSubscription = card.dataset.isSubscription === 'true';
    const sellingPlanInput = findSellingPlanInput(radioInput);

    if (!sellingPlanInput) return;

    if (isSubscription) {
      // Get the frequency dropdown for this option
      const frequencySelect = card.querySelector('.frequency-select');
      if (frequencySelect && frequencySelect.value) {
        sellingPlanInput.value = frequencySelect.value;
      }
    } else {
      // Clear selling plan for non-subscription options
      sellingPlanInput.value = '';
    }
  });

  // Handle frequency dropdown changes
  document.addEventListener('change', (e) => {
    const frequencySelect = e.target;
    if (!frequencySelect.classList.contains('frequency-select')) return;

    const card = frequencySelect.closest('.variant-option__radio-card');
    if (!card) return;

    const radioInput = card.querySelector('.variant-option__radio-input');
    const sellingPlanInput = findSellingPlanInput(frequencySelect);

    // Only update if this subscription option is currently selected
    if (radioInput && radioInput.checked && sellingPlanInput) {
      sellingPlanInput.value = frequencySelect.value;
      devLog('Selling plan updated:', frequencySelect.value);
    }
  });

  // Initialize selling plan for every picker that already has a subscription
  // option pre-selected — not just the first one on the page.
  const initCheckedSubscriptions = () => {
    const checkedSubscriptions = document.querySelectorAll(
      '.variant-option__radio-card[data-is-subscription="true"] .variant-option__radio-input:checked'
    );

    checkedSubscriptions.forEach((radioInput) => {
      const card = radioInput.closest('.variant-option__radio-card');
      const frequencySelect = card && card.querySelector('.frequency-select');
      const sellingPlanInput = findSellingPlanInput(radioInput);

      if (frequencySelect && sellingPlanInput && frequencySelect.value) {
        sellingPlanInput.value = frequencySelect.value;
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCheckedSubscriptions);
  } else {
    // The module can be evaluated after DOMContentLoaded has already fired
    // (dynamic import, cached module, section re-render), in which case the
    // listener above would never run and the plan would stay empty.
    initCheckedSubscriptions();
  }

  // Last line of defence: write the plan into the hidden input at submit time.
  // Vue re-checks radios programmatically (no change event) and sections can be
  // re-rendered after our listeners ran, so the input can be stale by the time
  // product-form-component builds its FormData. Capture phase runs before the
  // component's own submit handler.
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;

      const sellingPlanInput = form.querySelector('[data-selling-plan-input]');
      if (!sellingPlanInput) return;

      // Only touch the input when this form's product actually renders
      // subscription radio cards — other widgets own it otherwise.
      const scope = form.closest('.shopify-section, dialog, product-card') || document;
      const subscriptionCards = scope.querySelectorAll(
        '.variant-option__radio-card[data-is-subscription="true"]'
      );
      if (!subscriptionCards.length) return;

      let planId = '';
      subscriptionCards.forEach((card) => {
        const radioInput = card.querySelector('.variant-option__radio-input');
        if (!radioInput || !radioInput.checked) return;
        // Landing pages render several product blocks in one scope — keep only
        // the card that resolves to this form's hidden input.
        if (findSellingPlanInput(radioInput) !== sellingPlanInput) return;

        const frequencySelect = card.querySelector('.frequency-select');
        if (frequencySelect && frequencySelect.value) planId = frequencySelect.value;
      });

      sellingPlanInput.value = planId;
      devLog('Selling plan synced on submit:', planId || '(one-time)');
    },
    true
  );
}

// Initialize selling plan handlers
initSellingPlanHandlers();

// Initialize when DOM is ready and Vue is loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (typeof Vue !== 'undefined') {
      initVariantPickers();
    } else {
      window.addEventListener('vue-loaded', initVariantPickers);
    }
  });
} else {
  if (typeof Vue !== 'undefined') {
    initVariantPickers();
  } else {
    window.addEventListener('vue-loaded', initVariantPickers);
  }
}

// Image Grid Multi-Select Handlers
function initImageGridMultiSelect() {
  const multiSelectContainers = document.querySelectorAll('.variant-option--image-grid-multiselect');
  
  multiSelectContainers.forEach(container => {
    const optionName = container.dataset.optionName;
    const gridItems = container.querySelectorAll('.variant-option__grid-item');
    
    gridItems.forEach(gridItem => {
      const optionValue = gridItem.dataset.optionValue;
      const minusBtn = gridItem.querySelector('.variant-option__quantity-btn--minus');
      const plusBtn = gridItem.querySelector('.variant-option__quantity-btn--plus');
      const quantityDisplay = gridItem.querySelector('.variant-option__quantity-display');
      const quantitySelector = gridItem.querySelector('.variant-option__quantity-selector');
      const hiddenInput = gridItem.querySelector('.variant-option__multiselect-input');
      const isOutOfStock = /** @type {HTMLElement} */ (gridItem).dataset.outOfStock === 'true';

      // Disable plus button entirely if out of stock
      if (isOutOfStock && plusBtn) {
        /** @type {HTMLButtonElement} */ (plusBtn).disabled = true;
        plusBtn.classList.add('opacity-30', 'cursor-not-allowed');
        plusBtn.classList.remove('cursor-pointer');
      }

      let quantity = 0;
      
      // Update display and data attribute
      const updateQuantity = (newQuantity) => {
        quantity = Math.max(0, newQuantity);
        quantityDisplay.textContent = quantity;
        gridItem.dataset.quantity = quantity;
        
        // Update hidden input
        if (hiddenInput) {
          hiddenInput.value = quantity > 0 ? `${optionValue}:${quantity}` : '';
        }
        
        // Enable/disable minus button and update border
        if (quantity === 0) {
          minusBtn.disabled = true;
          minusBtn.classList.add('opacity-30', 'cursor-not-allowed');
          minusBtn.classList.remove('cursor-pointer');
          // Remove border-2 when quantity is 0
          if (quantitySelector) {
            quantitySelector.classList.remove('border-2');
          }
        } else {
          minusBtn.disabled = false;
          minusBtn.classList.remove('opacity-30', 'cursor-not-allowed');
          minusBtn.classList.add('cursor-pointer');
          // Add border-2 when quantity > 0
          if (quantitySelector) {
            quantitySelector.classList.add('border-2');
          }
        }
        
        // Dispatch multi-select change event
        dispatchMultiSelectChange(container, optionName);
      };
      
      // Plus button handler
      plusBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        updateQuantity(quantity + 1);
      });
      
      // Minus button handler
      minusBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (quantity > 0) {
          updateQuantity(quantity - 1);
        }
      });
      
      // Initialize
      updateQuantity(0);

      // Override flavor name with SKU title if show_sku_title is enabled
      const showSkuTitle = gridItem.dataset.showSkuTitle === 'true';
      const flavorNameElement = gridItem.querySelector('.text-small-body-bold.whitespace-nowrap');
      if (showSkuTitle && flavorNameElement) {
        const variantSku = gridItem.dataset.variantSku;
        const skuTitle = getFlavorTitleBySKU(variantSku);
        if (skuTitle) {
          // Store original title if not already stored
          if (!gridItem.dataset.originalTitle) {
            gridItem.dataset.originalTitle = flavorNameElement.textContent.trim();
          }
          flavorNameElement.textContent = skuTitle;
        } else if (gridItem.dataset.originalTitle) {
          // Restore original title if no SKU match
          flavorNameElement.textContent = gridItem.dataset.originalTitle;
        }
      } else if (flavorNameElement && gridItem.dataset.originalTitle) {
        // Restore original title if setting is disabled
        flavorNameElement.textContent = gridItem.dataset.originalTitle;
      }
    });
  });
}

// Dispatch multi-select change event
function dispatchMultiSelectChange(container, optionName) {
  const gridItems = container.querySelectorAll('.variant-option__grid-item');
  const selections = [];
  
  gridItems.forEach(item => {
    const quantity = parseInt(item.dataset.quantity || '0');
    if (quantity > 0) {
      selections.push({
        value: item.dataset.optionValue,
        quantity: quantity
      });
    }
  });
  
  const totalQuantity = selections.reduce((sum, s) => sum + s.quantity, 0);
  
  // Update the multiselect info display
  const multiselectInfo = document.querySelector('[data-multiselect-info]');
  const multiselectCount = document.querySelector('[data-multiselect-count]');
  
  if (multiselectInfo && multiselectCount) {
    if (totalQuantity > 0) {
      multiselectInfo.classList.remove('hidden');
      multiselectCount.textContent = totalQuantity;
    } else {
      multiselectInfo.classList.add('hidden');
    }
  }
  
  // Dispatch custom event for multi-select
  const event = new CustomEvent('variant:multiselect-change', {
    detail: {
      optionName: optionName,
      selections: selections,
      totalQuantity: totalQuantity
    },
    bubbles: true,
    composed: true
  });
  
  container.dispatchEvent(event);
  
  devLog('Multi-select changed:', {
    optionName,
    selections,
    totalQuantity: totalQuantity
  });
}

// Image Grid Single-Select Handlers
function initImageGridSingle() {
  const singleSelectContainers = document.querySelectorAll('.variant-option--image-grid-single');
  
  singleSelectContainers.forEach(container => {
    const optionItems = container.querySelectorAll('[data-variant-option]');
    
    optionItems.forEach(item => {
      const radioInput = item.querySelector('input[type="radio"], input[type="hidden"]');
      const isDisabled = item.dataset.available === 'false';
      
      if (!radioInput || isDisabled) return;
      
      // Handle click on the card
      item.addEventListener('click', function(e) {
        // Don't trigger if clicking the radio directly (it will handle itself)
        if (e.target === radioInput) return;
        
        // Remove selected state from all items in this container
        optionItems.forEach(otherItem => {
          otherItem.classList.remove('border-2', 'border-[#1E4036]', 'selected', 'bg-white');
        });
        
        // Add selected state to clicked item
        item.classList.add('border-2', 'border-[#1E4036]', 'selected', 'bg-white');
        
        // Check the radio
        radioInput.checked = true;
        
        // Trigger change event for Vue to pick up
        radioInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
      
      // Handle radio input change directly
      radioInput.addEventListener('change', function() {
        if (this.checked) {
          // Remove selected state from all items
          optionItems.forEach(otherItem => {
            otherItem.classList.remove('border-2', 'border-[#1E4036]', 'selected', 'bg-white');
          });
          
          // Add selected state to this item
          item.classList.add('border-2', 'border-[#1E4036]', 'selected', 'bg-white');
        }
      });
    });
  });
}

// Initialize both multi-select and single-select on DOM ready
function initImageGridVariants() {
  initImageGridMultiSelect();
  initImageGridSingle();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initImageGridVariants);
} else {
  initImageGridVariants();
}

// Export for potential reuse
if (!customElements.get('variant-picker-vue')) {
  customElements.define('variant-picker-vue', class extends HTMLElement {
    connectedCallback() {
      // Component initialization handled by Vue
      // Re-initialize image grid variants if needed
      setTimeout(() => {
        initImageGridVariants();
      }, 100);
    }

    disconnectedCallback() {
      // Cleanup Vue app if exists
      if (this.__vueApp) {
        this.__vueApp.unmount();
        delete this.__vueApp;
      }
    }
  });
}

// Initialize variant picker radio description SKU matching
document.addEventListener('DOMContentLoaded', () => {
  const radioCards = document.querySelectorAll('.variant-option__radio-card[data-variant-sku]');
  
  radioCards.forEach(card => {
    const sku = card.dataset.variantSku;
    const description = getFlavorDescriptionBySKU(sku);
    
    if (description) {
      const descriptionContent = card.querySelector('[data-description-content]');
      const descriptionSection = card.querySelector('[data-description-section]');
      const descriptionDivider = card.querySelector('[data-description-divider]');
      
      if (descriptionContent) {
        descriptionContent.textContent = description;
      }
      
      if (descriptionSection) {
        descriptionSection.style.display = '';
        card.dataset.hasDescription = 'true';
      }
      
      if (descriptionDivider) {
        descriptionDivider.style.display = '';
      }
      
      // Handle expand/collapse button
      const expandBtn = card.querySelector('.variant-option__expand-btn');
      if (expandBtn && descriptionSection) {
        expandBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          const isExpanded = descriptionSection.classList.contains('expanded');
          
          if (isExpanded) {
            descriptionSection.classList.remove('expanded');
            expandBtn.setAttribute('aria-label', 'Expand description');
          } else {
            descriptionSection.classList.add('expanded');
            expandBtn.setAttribute('aria-label', 'Collapse description');
          }
        });
      }
    }
  });
});
